import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import includes from '@nullvoxpopuli/ember-composable-helpers/helpers/includes';
import type { EmberChangeset } from 'ember-changeset';
import changeset from 'ember-changeset/helpers/changeset';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import t from 'ember-intl/helpers/t';
import add from 'ember-math-helpers/helpers/add';
import ModelList from 'parog-web/components/model-list';
import type FollowUpReason from 'parog-web/models/follow-up-reason';
import Checkbox from 'ui-kit/components/changeset/checkbox';
import Dropdown from 'ui-kit/components/changeset/dropdown';
import DatePicker from '../../changeset/date-picker';

interface Signature {
  Args: {
    changeset: EmberChangeset;
    followUpReasonId: FollowUpReason['id'];
  };
}

export default class TreatmentHistoryEditionFormBreastCancerFormSurgerySection extends Component<Signature> {
  get surgeryTypes(): Array<string> {
    switch (this.args.followUpReasonId) {
      case 'onco-lung-cancer':
        return ['pulmonary', 'cerebral', 'adrenal'];
      case 'onco-pancreatic-cancer':
        return [
          'prostatectomy',
          'lymph-node-dissection',
          'pulpectomy',
          'orchiectomy'
        ];
      default:
        return [
          'auxiliary-cleaning',
          'mastectomy',
          'quadrantectomy',
          'tumorectomy',
          'oncoplasty',
          'pamectomy',
          'prophylactic-surgery',
          'enlargement',
          'reconstruction',
          'sentinel-node',
          'pac-installation',
          'prophylactic-annexectomy',
          'ambulatory',
          'hospitalisation',
          'external',
          'prostatectomy',
          'lymph-node-dissection',
          'pulpectomy',
          'orchiectomy'
        ];
    }
  }

  get #surgeries() {
    return this.args.changeset.get('surgerySurgeries');
  }

  onChangesetUpdate = (changeset: EmberChangeset) => {
    changeset.execute();
    this.#setAsDirty();
  };

  #setAsDirty() {
    this.args.changeset.set('surgerySurgeries', [].concat(this.#surgeries));
  }

  <template>
    <h2 class='ui dividing header'>Chirurgie</h2>

    <div class='field'>
      <div class='field' id='surgerySurgeries' data-test-surgery-list>
        <ModelList
          @model={{@changeset}}
          @propertyName='surgerySurgeries'
          @addButtonTitle='Ajouter une chirurgie'
          as |Item|
        >
          {{#let (changeset Item.item) as |surgeryChangeset|}}
            <div class='ui segment' data-test-surgery-list-item={{Item.index}}>
              <h3 class='ui dividing header'>Chirurgie
                {{add Item.index 1}}</h3>
              <i
                class='trash icon absolute right-0 top-0 m-[0.5em] opacity-[0.5]'
                role='button'
                {{on 'click' Item.destroy}}
              >
              </i>
              <div>

                <div class='field'>
                  <DatePicker
                    @labelName='Consultation chirurgie'
                    @propertyName='consultation'
                    @changeset={{surgeryChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                <div class='field'>
                  <DatePicker
                    @labelName='Date de la chirurgie'
                    @propertyName='date'
                    @changeset={{surgeryChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                <div class='field'>
                  <Dropdown
                    @labelName='Type de chirurgie'
                    @propertyName='types'
                    @changeset={{surgeryChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                    @multipleSelection={{true}}
                    as |dropdown|
                  >
                    {{#each this.surgeryTypes as |surgeryType|}}
                      <dropdown.Item
                        @key={{surgeryType}}
                        @value={{t (concat 'surgery-type.' surgeryType)}}
                      />
                    {{/each}}
                  </Dropdown>
                </div>

                {{#if
                  (includes
                    'reconstruction' (changesetGet surgeryChangeset 'types')
                  )
                }}
                  <div class='field'>
                    <Dropdown
                      @labelName='Type de reconstruction'
                      @propertyName='reconstructionType'
                      @changeset={{surgeryChangeset}}
                      @onChangesetUpdate={{this.onChangesetUpdate}}
                      @multipleSelection={{true}}
                      as |dropdown|
                    >
                      <dropdown.Item
                        @key='musculo-skin-flap'
                        @value={{t
                          'surgery-reconstruction-type.musculo-skin-flap'
                        }}
                      />
                      <dropdown.Item
                        @key='prosthesis'
                        @value={{t 'surgery-reconstruction-type.prosthesis'}}
                      />
                      <dropdown.Item
                        @key='flap-prosthesis'
                        @value={{t
                          'surgery-reconstruction-type.flap-prosthesis'
                        }}
                      />
                      <dropdown.Item
                        @key='exclusive-lipomodelling'
                        @value={{t
                          'surgery-reconstruction-type.exclusive-lipomodelling'
                        }}
                      />
                      <dropdown.Item
                        @key='complementary-lipomodelling'
                        @value={{t
                          'surgery-reconstruction-type.complementary-lipomodelling'
                        }}
                      />
                      <dropdown.Item
                        @key='immediate'
                        @value={{t 'surgery-reconstruction-type.immediate'}}
                      />
                      <dropdown.Item
                        @key='deferred'
                        @value={{t 'surgery-reconstruction-type.deferred'}}
                      />
                    </Dropdown>
                  </div>
                  <div class='field'>
                    <Checkbox
                      @payloadKey='areolaConservation'
                      @labelName='Conservation aréole'
                      @changeset={{surgeryChangeset}}
                      @onChangesetUpdate={{this.onChangesetUpdate}}
                    />
                  </div>
                {{/if}}

                <div class='field'>
                  <DatePicker
                    @labelName='Consultation post chirurgie'
                    @propertyName='postConsultation'
                    @changeset={{surgeryChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                  />
                </div>
                <div class='field'>
                  <Dropdown
                    @labelName='Résécabilité'
                    @propertyName='resectability'
                    @changeset={{surgeryChangeset}}
                    @onChangesetUpdate={{this.onChangesetUpdate}}
                    as |dropdown|
                  >
                    <dropdown.Item @key='yes' @value='Oui' />
                    <dropdown.Item @key='no' @value='Non' />
                  </Dropdown>
                </div>
              </div>
            </div>
          {{/let}}
        </ModelList>
      </div>
    </div>
  </template>
}
